import React, { useEffect, useRef } from 'react';
import { LiquidMetal } from '@paper-design/shaders-react';
import { AbercrombysLogo } from './components/AbercrombysLogo';
import { HarcourtStreetMap } from './components/HarcourtStreetMap';
export function App() {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.5; // Slow down to half speed
    }
  }, []);
  useEffect(() => {
    // Google Analytics GA4
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-YXP00YXL7L';
    document.head.appendChild(script);
    const inlineScript = document.createElement('script');
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-YXP00YXL7L');
    `;
    document.head.appendChild(inlineScript);
    return () => {
      document.head.removeChild(script);
      document.head.removeChild(inlineScript);
    };
  }, []);
  return <div className="min-h-screen bg-white selection:bg-black selection:text-white overflow-x-hidden" data-id="element-0" data-mp="3ml1397n9rmih">
      {/* Navigation - Transparent & Seamless */}
      <nav className="absolute top-0 w-full z-50 px-6 md:px-12 pt-[calc(1.5rem+env(safe-area-inset-top))] pb-6 flex justify-between items-center gap-4" data-id="element-1" data-mp="3rd189ei755lx">
        <AbercrombysLogo className="h-3 md:h-4 w-auto text-white [filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.35))]" data-id="element-2" data-mp="2zlv6yuqqp1v2" />
        <a href="mailto:jockl@abercrombys.com.au" onClick={() => {
        if (typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'enquire_click', {
            property: '1 Harcourt Street'
          });
        }
      }} className="border border-white text-white bg-transparent px-4 md:px-5 py-3 min-h-[44px] font-body text-[10px] tracking-widest uppercase whitespace-nowrap hover:bg-white hover:text-black active:bg-white active:text-black transition-colors duration-300 flex items-center justify-center" data-id="element-3" data-mp="33ngf62sbwryx">
          Enquire Now
        </a>
      </nav>

      {/* Hero Section */}
      <section className="w-full min-h-[100svh] bg-white flex flex-col" data-id="element-4" data-mp="d4nwdiwxho3g">
        {/* Video Background — top block */}
        <div className="relative w-full h-[65svh] md:h-[75svh] overflow-hidden bg-gray-100 shrink-0" data-id="element-5" data-mp="851lhg4kojxt">
          <video ref={videoRef} autoPlay loop muted playsInline className="hero-drift absolute top-[-5%] left-[-5%] w-[110%] h-[110%] object-cover" data-id="element-6" data-mp="3gkv1ltuzrtec">
            <source src="https://cdn.magicpatterns.com/uploads/sniFfQYVPigZFALJACGxdd/VEO,%20HAWTHORN.mp4" type="video/mp4" data-id="element-7" data-mp="3qcqzp4x2vjik" />
          </video>
          {/* Top shade for nav legibility */}
          <div className="absolute top-0 inset-x-0 h-40 bg-gradient-to-b from-black/30 to-transparent z-10 pointer-events-none" data-id="element-8" data-mp="2xsk1u8vmwbkg"></div>
          {/* Bottom fade into the white text block */}
          <div className="absolute bottom-0 inset-x-0 h-32 md:h-48 bg-gradient-to-t from-white via-white/60 to-transparent z-10 pointer-events-none" data-id="element-9" data-mp="18boigguw01ub"></div>
        </div>

        {/* Bottom Content — on solid white space */}
        <div className="flex-1 flex flex-col justify-center px-6 md:px-12 py-10 md:py-12 z-20" data-id="element-10" data-mp="13eoecbhll95u">
          <div className="max-w-7xl mx-auto w-full flex flex-col md:flex-row justify-between items-start md:items-end gap-8" data-id="element-11" data-mp="1r497tobdnotd">
            <div className="flex flex-col items-start max-w-2xl" data-id="element-12" data-mp="1w3lua7yc6tvf">
              <div className="flex flex-wrap items-center gap-4 mb-4 md:mb-5" data-id="element-13" data-mp="1i3kukvtt5shj">
                <p className="font-body text-[9px] md:text-[10px] text-brand-ink tracking-widest uppercase bg-brand-wash px-4 md:px-5 py-2.5" data-id="element-14" data-mp="1i5ninabehbjm">
                  For Sale · Private Sale
                </p>
                <p className="font-body text-[10px] text-black tracking-widest uppercase" data-id="element-15" data-mp="6qiahqsi2ksm">
                  Hawthorn East, Victoria
                </p>
              </div>
              <h1 className="font-display font-normal text-[2.75rem] sm:text-5xl md:text-6xl lg:text-7xl text-black leading-[1.03] mb-4 md:mb-5" data-id="element-16" data-mp="qyqavo0wi78c">
                1 Harcourt <br className="hidden sm:block" data-id="element-17" data-mp="h6syidy5p981" />
                <span className="italic text-gray-muted" data-id="element-18" data-mp="h7lx2rmyosf3">Street.</span>
              </h1>
              <p className="font-body font-light text-gray-dark text-sm md:text-base leading-relaxed max-w-lg" data-id="element-19" data-mp="c9u6c0y1zcch">
                A landmark John Beswicke residence of the Queen Anne era, held
                on 4,198sqm approx. of landscaped grounds.
              </p>
            </div>

            {/* Scroll Indicator */}
            <div className="hidden md:flex flex-col items-center gap-2.5 shrink-0 pb-1" data-id="element-20" data-mp="zzwtc2n41s48">
              <div className="w-[1px] h-12 bg-black/10 relative overflow-hidden" data-id="element-21" data-mp="vvyxq1wzuzfx">
                <div className="absolute top-0 left-0 w-full h-full bg-black animate-[scroll_2s_ease-in-out_infinite]" data-id="element-22" data-mp="3kpm8yhvn1hxz"></div>
              </div>
              <span className="font-body text-[10px] text-black tracking-[0.22em] uppercase [writing-mode:vertical-rl] rotate-180" data-id="element-23" data-mp="1w4f4gqt7xhon">
                Discover
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Key Details Strip */}
      <section className="bg-white border-b border-black/5 relative z-20" data-id="element-24" data-mp="253sebkl9889w">
        <div className="grid grid-cols-2 md:grid-cols-4" data-id="element-25" data-mp="29wlh42x5x6jc">
          {[{
          value: '7',
          label: 'Bedrooms'
        }, {
          value: '3',
          label: 'Bathrooms'
        }, {
          value: '4,198',
          label: 'SQM Land'
        }, {
          value: 'Private',
          label: 'Sale Status'
        }].map((stat, i) => <div key={i} className={`py-10 md:py-12 px-4 md:px-6 flex flex-col items-center justify-center text-center ${i !== 3 ? 'md:border-r border-black/5' : ''} ${i % 2 === 0 ? 'border-r border-black/5' : ''} ${i < 2 ? 'border-b border-black/5 md:border-b-0' : ''}`} data-id="element-26" data-mp="ti3dxbd8m2eo" data-mpi={i}>
              <span className="font-display text-3xl md:text-4xl text-black mb-2 md:mb-3" data-id="element-27" data-mp="1wtiyp8uq4f0a">
                {stat.value}
              </span>
              <span className="font-body text-[10px] text-gray-muted tracking-widest uppercase" data-id="element-28" data-mp="2aq5wfdui3979">
                {stat.label}
              </span>
            </div>)}
        </div>
      </section>

      {/* Property Description */}
      <section className="bg-offWhite py-16 md:py-32 px-6 md:px-12" data-id="element-29" data-mp="2frva7193y0z1">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-24" data-id="element-30" data-mp="2fpiqojnpbijv">
          <div className="md:col-span-5" data-id="element-31" data-mp="2tkkvt4kq53vm">
            <div className="flex items-center gap-4 mb-6 md:mb-8" data-id="element-32" data-mp="2pgwngjo9q49b">
              <div className="w-8 h-[1px] bg-black" data-id="element-33" data-mp="33gxobz4j9bos"></div>
              <span className="font-body text-[10px] text-black tracking-widest uppercase" data-id="element-34" data-mp="33hfqtdhsgm55">
                The Vision
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-black leading-[1.1]" data-id="element-35" data-mp="3i33ev9r44ata">
              The <br data-id="element-36" data-mp="3n2fyzmqln3s6" />
              <span className="italic text-gray-muted" data-id="element-37" data-mp="3n2zk1cbjq8af">Residence.</span>
            </h2>
          </div>
          <div className="md:col-span-7 flex items-center" data-id="element-38" data-mp="384f8j88hpk5f">
            <p className="font-body font-light text-gray-dark text-base md:text-lg leading-[1.8] md:leading-[2] md:pl-12 md:border-l border-black/10" data-id="element-39" data-mp="3d4jqz3sykoya">
              Designed by renowned architect John Beswicke, this is a fine
              example of the Queen Anne era with a Tudor style profile, held on
              4,198sqm approx. A gracious reception foyer leads to formal and
              informal rooms of grand scale beneath high, detailed ceilings,
              while a majestic staircase and breathtaking stained glass window
              ascend to a spacious central retreat surrounded by up to seven
              bedrooms.
            </p>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="features-wash py-16 md:py-32 px-6 md:px-12" data-id="element-40" data-mp="30vnd65zs1mei">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-24" data-id="element-41" data-mp="2vwanzelfo38r">
          <div className="md:col-span-5" data-id="element-42" data-mp="2qx7jpwda211u">
            <div className="md:sticky md:top-32" data-id="element-43" data-mp="2qw69fu435il6">
              <div className="flex items-center gap-4 mb-6 md:mb-8" data-id="element-44" data-mp="2qz0p78c5mf89">
                <div className="w-8 h-[1px] bg-brand-ink" data-id="element-45" data-mp="2lxuxndu8cy0b"></div>
                <span className="font-body text-[10px] text-brand-ink tracking-widest uppercase" data-id="element-46" data-mp="2hwrh0p4cyxy1">
                  The Detail
                </span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl text-black leading-[1.1]" data-id="element-47" data-mp="286pui1rnlnl8">
                Curated <br data-id="element-48" data-mp="237n15el1d5t3" />
                <span className="italic text-brand-ink" data-id="element-49" data-mp="23851amatk46a">Features.</span>
              </h2>
            </div>
          </div>
          <div className="md:col-span-7" data-id="element-50" data-mp="1y78plmk87dqb">
            <ul className="flex flex-col border-t border-brand-ink/15" data-id="element-51" data-mp="1yad011oab5pf">
              {['Up to seven bedrooms surrounding a spacious central upstairs retreat', 'Majestic staircase beneath a breathtaking stained glass window', 'Attic and tower with magnificent city views, allowing for future expansion', 'Heated swimming pool and floodlit north–south synthetic grass tennis court', 'Expansive landscaped grounds with superb spaces for alfresco dining', 'Large free-standing summer house, fully self contained', 'Secure remote gate access from both Auburn Road and Harcourt Street to multi-car garaging', 'Moments to Auburn Village and station, Glenferrie Road, Camberwell Junction and elite schools'].map((item, i) => <li key={i} className="py-5 md:py-6 border-b border-brand-ink/15 last:border-0 flex flex-row items-start md:items-center gap-4 md:gap-8 group" data-id="element-52" data-mp="4re2gd9hhdab" data-mpi={i}>
                  <span className="font-display italic text-xl md:text-2xl text-brand-ink/45 group-hover:text-brand-ink transition-colors duration-300 mt-0.5 md:mt-0" data-id="element-53" data-mp="38d1w65e8t0s3">
                    0{i + 1}
                  </span>
                  <span className="font-body font-light text-gray-dark text-sm md:text-base leading-relaxed group-hover:text-black transition-colors duration-300" data-id="element-54" data-mp="2p5b8f8xscves">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-offWhite py-16 md:py-32 px-6 md:px-12" data-id="element-55" data-mp="21exh5kn0z0l9">
        <div className="max-w-7xl mx-auto" data-id="element-56" data-mp="26etg35gv81ty">
          <div className="flex items-center justify-center gap-4 mb-10 md:mb-16" data-id="element-57" data-mp="26e1o04mh8m8f">
            <div className="w-8 h-[1px] bg-black" data-id="element-58" data-mp="2b5s4ksc0c68a"></div>
            <span className="font-body text-[10px] text-black tracking-widest uppercase" data-id="element-59" data-mp="2b7cycfqhzl53">
              Visuals
            </span>
            <div className="w-8 h-[1px] bg-black" data-id="element-60" data-mp="1iopd90bi4640"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-16" data-id="element-61" data-mp="3vzihly7tltmt">
            {['https://cdn.magicpatterns.com/uploads/fkJdgnw96Lm6mKrbS2w5hQ/01810823_img_01.jpg', 'https://cdn.magicpatterns.com/uploads/tzJWXWTechQy5mgFSayMN2/01810823_img_02.jpg', 'https://cdn.magicpatterns.com/uploads/6ie3yXbMSPjLbqZq8gkqH3/01810823_img_05.jpg'].map((url, i) => <div key={i} className="group relative overflow-hidden w-full" data-id="element-62" data-mp="3d0wekdsqtsg9" data-mpi={i}>
                <div className="aspect-[4/3] md:aspect-[4/5] w-full bg-gray-light border border-black/5 transition-transform duration-300 ease-out md:group-hover:scale-105" data-id="element-63" data-mp="388d3bnhj39em">
                  <img src={url} alt={`Property visual ${i + 1}`} className="w-full h-full object-cover" data-id="element-64" data-mp="2za1632m5pdd7" />
                </div>
              </div>)}
          </div>
          <p className="font-body text-[10px] text-gray-muted tracking-widest uppercase text-center max-w-2xl mx-auto leading-loose" data-id="element-65" data-mp="2pdejo9e6me92">
            Full photography, floor plan and property memorandum available upon
            approved enquiry.
          </p>
        </div>
      </section>

      {/* Location */}
      <HarcourtStreetMap data-id="element-66" data-mp="2aod82b0uskxj" />

      {/* Agent Section */}
      <section className="agent-wash py-16 md:py-32 px-6 md:px-12" data-id="element-67" data-mp="2aovbbrlxnzey">
        <div className="max-w-7xl mx-auto" data-id="element-68" data-mp="2ydnyozohoyxy">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-24 items-center" data-id="element-69" data-mp="2yepknz71n5ug">
            <div className="md:col-span-5 relative" data-id="element-70" data-mp="33c90h3je3qoi">
              <div className="aspect-[3/4] w-full max-w-[280px] md:max-w-sm mx-auto bg-white border border-brand-ink/15 relative z-10 overflow-hidden" data-id="element-71" data-mp="2pm5mezunz3a5">
                <img src="https://cdn.magicpatterns.com/uploads/oxCFzWzVmsZcmT2vsYDkZT/Jock-Langley-scaled.webp" alt="Jock Langley" className="w-full h-full object-cover" data-id="element-72" data-mp="2tleapp6xq8yb" />
              </div>
              {/* Decorative editorial frame */}
              <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-12 w-full max-w-[280px] md:max-w-sm h-full border border-brand-ink/25 z-0 hidden md:block" data-id="element-73" data-mp="1am6odh04r42r"></div>
            </div>

            <div className="md:col-span-7 flex flex-col text-center md:text-left" data-id="element-74" data-mp="1kg71o65kffmk">
              <div className="flex items-center justify-center md:justify-start gap-4 mb-4 md:mb-6" data-id="element-75" data-mp="1fg1fv6lz8nce">
                <div className="w-8 h-[1px] bg-brand-ink" data-id="element-76" data-mp="rpxbgx0ysjkc"></div>
                <span className="font-body text-[10px] text-brand-ink tracking-widest uppercase block" data-id="element-77" data-mp="mrnjsweg813h">
                  Exclusive Representation
                </span>
              </div>
              <h3 className="font-display text-4xl md:text-5xl text-black mb-2 md:mb-3" data-id="element-78" data-mp="woiet5bijjd0">
                Jock Langley
              </h3>
              <p className="font-display italic text-xl md:text-2xl text-brand-ink mb-8 md:mb-10" data-id="element-79" data-mp="2d16xperwk4r7">
                Director, Abercrombys
              </p>

              <div className="flex flex-col gap-4" data-id="element-80" data-mp="hnd0l5it8cuy">
                <a href="tel:+61419530008" className="font-body font-light text-sm md:text-base text-black hover:text-gray-muted transition-colors duration-300 flex items-center justify-center md:justify-start gap-4 md:gap-6 group min-h-[44px]" data-id="element-81" data-mp="howfarotifum">
                  <span className="w-8 h-[1px] bg-brand-ink/40 group-hover:w-12 transition-[width] duration-300 hidden md:block" data-id="element-82" data-mp="vkhygi8de5f4"></span>
                  +61 419 530 008
                </a>
                <a href="mailto:jockl@abercrombys.com.au" className="font-body font-light text-sm md:text-base text-black hover:text-gray-muted transition-colors duration-300 flex items-center justify-center md:justify-start gap-4 md:gap-6 group min-h-[44px]" data-id="element-83" data-mp="15ixt2v6lvwtd">
                  <span className="w-8 h-[1px] bg-brand-ink/40 group-hover:w-12 transition-[width] duration-300 hidden md:block" data-id="element-84" data-mp="1ja2n9hr7fwwb"></span>
                  jockl@abercrombys.com.au
                </a>
              </div>

              <p className="font-body font-light text-sm text-brand-ink mt-8 md:mt-10 leading-relaxed" data-id="element-85" data-mp="1tg3whxf9iqwe">
                For a private inspection or further information, contact us
                directly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white pt-12 md:pt-16 pb-10 md:pb-8 px-6 md:px-12 border-t border-black/5" data-id="element-86" data-mp="2h5ex9ewm4laj">
        <div className="max-w-7xl mx-auto" data-id="element-87" data-mp="2c63e5z8rx3ps">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 md:gap-4" data-id="element-88" data-mp="2c751p36z7ioe">
            <p className="font-body text-[10px] text-gray-muted tracking-widest uppercase text-center md:text-left max-w-xl leading-relaxed" data-id="element-89" data-mp="27ebyijr5oief">
              Offered for private sale. Price on application — all enquiries are
              treated in strict confidence.
            </p>
            <p className="font-body text-[10px] text-gray-muted/50 tracking-widest uppercase" data-id="element-90" data-mp="2v4o25ifdbsz7">
              © 2026 Abercrombys
            </p>
          </div>
        </div>
      </footer>

      {/* VAULT[LAB] CTA Footer */}
      <section className="relative overflow-hidden bg-black text-white pt-20 md:pt-28 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-[calc(7rem+env(safe-area-inset-bottom))] px-6 md:px-12" data-id="element-91" data-mp="y68m2wv6686u">
        {/* LiquidMetal Shader Background */}
        <div className="absolute inset-0 z-0 opacity-40 mix-blend-screen pointer-events-none flex items-center justify-center" data-id="element-92" data-mp="ahpdeg87jte8">
          <LiquidMetal speed={0.42} softness={0.1} repetition={1.94} shiftRed={0.3} shiftBlue={0.3} distortion={0.07} contour={0.79} scale={0.6} rotation={0} shape="circle" angle={70} frame={849368.655999263} colorBack="#00000000" colorTint="#C8FF00" style={{
          height: '100%',
          width: '100%',
          minHeight: '800px'
        }} data-id="element-93" data-mp="fhk4ajrzgiza" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center text-center" data-id="element-94" data-mp="3kexzbyr93ak6">
          {/* Logo */}
          <div className="flex items-baseline gap-1 mb-10 md:mb-12" data-id="element-95" data-mp="3fd8qmpri38z9">
            <div className="text-5xl md:text-[84px] text-white leading-none tracking-normal" style={{
            fontFamily: "'Bebas Neue', sans-serif"
          }} data-id="element-96" data-mp="3u4zn0pfoyqyk">
              VAULT
            </div>
            <div className="text-5xl md:text-[84px] text-[#C8FF00] leading-none tracking-normal" style={{
            fontFamily: "'Bebas Neue', sans-serif"
          }} data-id="element-97" data-mp="6ygw3afq44ep">
              [LAB]
            </div>
          </div>

          {/* Pitch line */}
          <p className="font-body max-w-3xl mb-10 md:mb-14" data-id="element-98" data-mp="gwwmfb6xf9n5">
            <span className="block font-light text-base md:text-xl text-white/75 leading-relaxed" data-id="element-99" data-mp="bybj32wdo9uh">
              If one of your listings came to mind while you were reading this —
            </span>
            <span className="block font-medium text-xl md:text-3xl text-white leading-snug mt-4 md:mt-5" data-id="element-100" data-mp="2dqrc5s4aa62r">
              that's the one.
            </span>
          </p>

          {/* CTA */}
          <a href="mailto:elliott@vaultlab.agency" className="group inline-flex items-center gap-4 bg-[#C8FF00] text-black px-7 md:px-8 py-5 min-h-[56px] font-body text-[11px] md:text-xs tracking-[0.2em] md:tracking-[0.25em] uppercase font-semibold whitespace-nowrap hover:bg-white active:bg-white transition-colors duration-300 ease-out" data-id="element-101" data-mp="1wv3qyg9y9ey4">
            Get In Touch
            <span className="w-6 h-[1px] bg-black group-hover:w-10 transition-[width] duration-300 ease-out" data-id="element-102" data-mp="1dw7yb9dqp8me"></span>
          </a>
        </div>
      </section>

      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes scroll {
            0% { transform: translateY(-100%); }
            50% { transform: translateY(0); }
            100% { transform: translateY(100%); }
          }
        `
    }} data-id="element-103" data-mp="1iygtmce34mro" />
    </div>;
}